import { useCallback, useEffect, useRef, useState } from 'react';

import type { DeviceStorage } from '@/features/persons/images/devices';
import { chooseCamera, rememberCamera, rememberedCamera } from '@/features/persons/images/devices';

/**
 * Opening a camera, and the four ways that fails.
 *
 * **Every failure mode here is one an operator meets in the first week**, so each is a state with a
 * sentence of its own rather than a caught exception rendered as "something went wrong": a terminal
 * with no camera at all, a browser whose permission has been denied, a camera that was unplugged
 * mid-shift, and everything else. The denial in particular has to name the browser setting, because
 * there is nothing this application can do about it and the person at the desk has to be told where
 * to go.
 *
 * **The platform is injected.** `navigator.mediaDevices` does not exist in `jsdom` and cannot be
 * driven from a test even where it does, so the three interesting cases — granted, denied, and the
 * device disappearing — would otherwise be untestable, which for a control that only misbehaves in
 * front of a person at a desk is the worst place to have no coverage.
 */

/** The slice of `navigator.mediaDevices` this module uses. */
export interface MediaDevicesLike {
  enumerateDevices: () => Promise<MediaDeviceInfo[]>;
  getUserMedia: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  addEventListener?: (type: 'devicechange', listener: () => void) => void;
  removeEventListener?: (type: 'devicechange', listener: () => void) => void;
}

/**
 * The platform's camera API, or `null` where this browser has none.
 *
 * The DOM types say `navigator.mediaDevices` is always there, and two environments this application
 * really runs in prove otherwise: a page served over plain HTTP from anything but localhost, where
 * the whole API is withheld as a non-secure context, and `jsdom`. A screen that assumed it exists
 * fails with a `TypeError` from inside an effect rather than saying "this terminal has no camera".
 *
 * **It lives here rather than beside one of its callers** because two screens now ask the question:
 * the record's photographs panel and the registration form's staged strip. Two copies of it is two
 * answers to "has this terminal a camera", and the second one drifts.
 */
export function platformMedia(): MediaDevicesLike | null {
  const platform: { mediaDevices?: MediaDevicesLike } = globalThis.navigator;
  return platform.mediaDevices ?? null;
}

/** What the camera is doing. Reachable from outside as `Camera['state']`. */
type CameraState =
  /** This browser exposes no camera API at all. */
  | { readonly kind: 'unsupported' }
  /** Not opened yet. Nothing is streaming and no permission has been asked for. */
  | { readonly kind: 'idle' }
  | { readonly kind: 'opening' }
  /** The operator, or their administrator, has refused this origin the camera. */
  | { readonly kind: 'denied' }
  /** The machine has no video input. */
  | { readonly kind: 'no_camera' }
  /** It was streaming and the device went away: unplugged, or claimed by another application. */
  | { readonly kind: 'lost' }
  | { readonly kind: 'live'; readonly stream: MediaStream }
  | { readonly kind: 'failed'; readonly name: string };

/** One camera the operator may choose. Reachable from outside as `Camera['options']`. */
interface CameraOption {
  readonly deviceId: string;
  readonly label: string;
}

export interface Camera {
  readonly state: CameraState;
  readonly options: readonly CameraOption[];
  readonly selected: string | null;
  readonly open: () => void;
  readonly close: () => void;
  readonly select: (deviceId: string) => void;
}

/** The DOMException names a refused camera arrives under, across the browsers this product runs in. */
const DENIED_NAMES = new Set(['NotAllowedError', 'SecurityError', 'PermissionDeniedError']);

/** The names that mean "there is no such camera" rather than "you may not have it". */
const ABSENT_NAMES = new Set(['NotFoundError', 'DevicesNotFoundError', 'OverconstrainedError']);

function stateForError(error: unknown): CameraState {
  const name = error instanceof Error ? error.name : 'Error';
  if (DENIED_NAMES.has(name)) {
    return { kind: 'denied' };
  }
  return ABSENT_NAMES.has(name) ? { kind: 'no_camera' } : { kind: 'failed', name };
}

export function useCamera(media: MediaDevicesLike | null, storage: DeviceStorage): Camera {
  const [state, setState] = useState<CameraState>(
    media === null ? { kind: 'unsupported' } : { kind: 'idle' },
  );
  const [options, setOptions] = useState<readonly CameraOption[]>([]);
  const [selected, setSelected] = useState<string | null>(() => rememberedCamera(storage));
  const stream = useRef<MediaStream | null>(null);
  // Which open attempt is current. A second press, a device change or an unmount makes every earlier
  // attempt stale, and a stale attempt that resolved would otherwise install a stream nobody is
  // watching and a state nobody asked for.
  const attempt = useRef(0);

  const stop = useCallback(() => {
    for (const track of stream.current?.getTracks() ?? []) {
      track.stop();
    }
    stream.current = null;
  }, []);

  // Every stream this hook opened is stopped when it unmounts. A camera left running is a lit
  // indicator light on a shared terminal nobody is using, which is the most alarming possible
  // version of a leak in a product that photographs people.
  useEffect(
    () => () => {
      attempt.current += 1;
      stop();
    },
    [stop],
  );

  const open = useCallback(() => {
    if (media === null) {
      return;
    }
    attempt.current += 1;
    const mine = attempt.current;
    const current = (): boolean => attempt.current === mine;
    setState({ kind: 'opening' });

    void (async () => {
      try {
        // The device list is read AFTER permission is granted, because a browser that has not been
        // granted the camera reports every device with an empty label — so a picker built from a
        // pre-permission enumeration offers "Camera 1" and "Camera 2" and nothing an operator can
        // tell apart.
        const remembered = rememberedCamera(storage);
        const first = await media.getUserMedia({
          video: remembered === null ? true : { deviceId: { ideal: remembered } },
          audio: false,
        });
        const devices = await media.enumerateDevices();
        const cameras = devices
          .filter((device) => device.kind === 'videoinput')
          .map((device) => ({ deviceId: device.deviceId, label: device.label }));

        if (!current()) {
          for (const track of first.getTracks()) {
            track.stop();
          }
          return;
        }

        stream.current = first;
        setOptions(cameras);
        setSelected(chooseCamera(cameras, remembered));
        setState({ kind: 'live', stream: first });

        // A track that ends on its own was not stopped by this application: the device was
        // unplugged, or another application took it. Reporting it as a state is what lets the
        // screen say so instead of showing a frozen last frame that looks live.
        for (const track of first.getVideoTracks()) {
          track.addEventListener('ended', () => {
            if (current()) {
              setState({ kind: 'lost' });
            }
          });
        }
      } catch (error) {
        if (current()) {
          setState(stateForError(error));
        }
      }
    })();
  }, [media, storage]);

  const close = useCallback(() => {
    attempt.current += 1;
    stop();
    setState(media === null ? { kind: 'unsupported' } : { kind: 'idle' });
  }, [media, stop]);

  const select = useCallback(
    (deviceId: string) => {
      rememberCamera(storage, deviceId);
      setSelected(deviceId);
      if (media === null) {
        return;
      }
      attempt.current += 1;
      const mine = attempt.current;
      stop();
      setState({ kind: 'opening' });
      void (async () => {
        try {
          const next = await media.getUserMedia({
            video: { deviceId: { exact: deviceId } },
            audio: false,
          });
          if (attempt.current !== mine) {
            for (const track of next.getTracks()) {
              track.stop();
            }
            return;
          }
          stream.current = next;
          setState({ kind: 'live', stream: next });
        } catch (error) {
          if (attempt.current === mine) {
            setState(stateForError(error));
          }
        }
      })();
    },
    [media, stop, storage],
  );

  return { state, options, selected, open, close, select };
}
